import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, Star, ShieldCheck, Building2 } from 'lucide-react';

type AgentRow = {
  id: string;
  user_id: string;
  photo_url: string | null;
  rating: number | null;
  total_reviews: number | null;
  total_listings: number | null;
  is_verified: boolean | null;
  areas_served: string[] | null;
  specializations: string[] | null;
  agencies: { id: string; name: string; city: string | null; logo_url: string | null } | null;
};

export default function Agents() {
  const [search, setSearch] = useState('');

  const { data: agents, isLoading } = useQuery<AgentRow[]>({
    queryKey: ['agents-directory'],
    queryFn: async () => {
      const { data } = await supabase
        .from('agent_profiles')
        .select('id, user_id, photo_url, rating, total_reviews, total_listings, is_verified, areas_served, specializations, agencies(id, name, city, logo_url)')
        .eq('is_verified', true)
        .order('rating', { ascending: false })
        .limit(200);
      return (data || []) as unknown as AgentRow[];
    },
  });

  const filtered = (agents || []).filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.agencies?.name?.toLowerCase().includes(q) ||
      a.agencies?.city?.toLowerCase().includes(q) ||
      a.specializations?.some((s) => s.toLowerCase().includes(q)) ||
      a.areas_served?.some((s) => s.toLowerCase().includes(q))
    );
  });

  return (
    <Layout>
      <SEOHead
        title="Real Estate Agents in Ethiopia"
        description="Browse verified real estate agents across Ethiopia. Find an agent by city, specialization or agency."
        canonical="/agents"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Real Estate Agents in Ethiopia',
          url: 'https://ethiorealestates.com/agents',
        }}
      />

      <section className="bg-gradient-to-br from-primary/10 via-background to-background border-b">
        <div className="container py-10">
          <h1 className="font-display text-3xl md:text-4xl font-bold">Verified Agents</h1>
          <p className="mt-2 text-muted-foreground">
            Connect with professional real estate agents across Ethiopia — all vetted through our verification process.
          </p>
          <div className="relative mt-5 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by city, agency or specialization…"
              className="pl-9"
            />
          </div>
        </div>
      </section>

      <section className="container py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full rounded-lg" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Building2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No agents match your search.</p>
            <p className="text-sm mt-1">
              Try browsing <Link to="/agencies" className="text-primary underline">verified agencies</Link> instead.
            </p>
          </div>
        ) : (
          <>
            <div className="text-sm text-muted-foreground mb-4">
              Showing <span className="font-semibold text-foreground">{filtered.length}</span> verified agents
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((a) => {
                const initials = (a.agencies?.name || 'A').slice(0, 2).toUpperCase();
                const card = (
                  <Card className="h-full hover:shadow-md hover:border-primary/40 transition-all">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-14 w-14">
                          <AvatarImage src={a.photo_url || a.agencies?.logo_url || undefined} />
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-semibold truncate">{a.agencies?.name || 'Independent Agent'}</h3>
                            {a.is_verified && <ShieldCheck className="h-4 w-4 text-primary shrink-0" />}
                          </div>
                          {a.agencies?.city && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">{a.agencies.city}</span>
                            </div>
                          )}
                          {(a.rating ?? 0) > 0 && (
                            <div className="flex items-center gap-1 text-xs mt-1">
                              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                              <span className="font-semibold">{Number(a.rating).toFixed(1)}</span>
                              <span className="text-muted-foreground">({a.total_reviews || 0})</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {a.specializations && a.specializations.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {a.specializations.slice(0, 3).map((s) => (
                            <Badge key={s} variant="secondary" className="text-[10px] font-normal">{s}</Badge>
                          ))}
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {a.total_listings || 0} listing{(a.total_listings || 0) === 1 ? '' : 's'}
                        </span>
                        <span className="text-primary font-semibold">View agency →</span>
                      </div>
                    </CardContent>
                  </Card>
                );
                return a.agencies?.id ? (
                  <Link key={a.id} to={`/agencies/${a.agencies.id}`}>{card}</Link>
                ) : (
                  <div key={a.id}>{card}</div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
